import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useCompanyStore } from '@/stores/company-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useSnackbarStore } from '@certara/common-react-components';

interface RenameResponse {
  IsSuccess: boolean;
  ErrorMessage?: string;
}

interface RenameProps {
  open: boolean;
  itemName?: string;
  onClose: () => void;
}

export function Rename({ open, itemName = '', onClose }: RenameProps) {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const { activeCompany } = useCompanyStore();
  const { currentPath } = useNavigationStore();
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setNewName(itemName);
      setError('');
    }
  }, [open, itemName]);

  const renameMutation = useMutation({
    mutationFn: async () => {
      if (!activeCompany) throw new Error('Company not selected');
      return apiClient.postForm<RenameResponse>('api/Storage/RenameFile', {
        companyId: activeCompany.Id,
        path: currentPath || '/',
        oldFileName: itemName,
        newFileName: newName,
      });
    },
    onSuccess: (response) => {
      if (!response?.IsSuccess) {
        setError(response?.ErrorMessage || 'Failed to rename item');
      } else {
        addSnackbar(`Renamed to "${newName}" successfully`, 'success');
        handleClose();
        queryClient.invalidateQueries({
          queryKey: ['gridData', activeCompany?.Id, currentPath],
        });
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      addSnackbar('Rename failed', 'error', message, true);
    },
  });

  const handleClose = () => {
    setNewName(itemName);
    setError('');
    onClose();
  };

  const handleRename = () => {
    setError('');

    if (!newName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    if (newName === itemName) {
      setError('New name must be different');
      return;
    }

    renameMutation.mutate();
  };

  return (
    <Dialog
      data-testid="rename-dialog"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minWidth: '400px' },
      }}
    >
      <DialogTitle data-testid="rename-dialog-title">Rename "{itemName}"</DialogTitle>
      <DialogContent>
        <TextField
          data-testid="rename-dialog-input"
          autoFocus
          fullWidth
          margin="dense"
          label="New name"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            setError('');
          }}
          error={Boolean(error)}
          helperText={error}
          disabled={renameMutation.isPending}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={renameMutation.isPending}
          data-testid="rename-dialog-cancel-button"
        >
          Cancel
        </Button>
        <Button
          data-testid="rename-dialog-submit-button"
          onClick={handleRename}
          variant="contained"
          disabled={renameMutation.isPending || newName === itemName || !newName.trim()}
          sx={{ minWidth: '100px' }}
        >
          {renameMutation.isPending ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
